import {
	Description,
	Dialog,
	DialogPanel,
	DialogTitle,
	Transition,
	TransitionChild,
} from '@headlessui/react';
import React, { Fragment } from 'react';
import { useThemeColors } from '../../providers/ThemeProvider';
import { cn } from '../../utils/cn';
import { Button } from '../forms/Button';

type ModalSize = 'sm' | 'md' | 'lg' | 'xl' | 'full';

/**
 * Where the modal panel anchors in the viewport.
 *   - 'center' (default): traditional centered dialog with scale-in
 *     transition. Use for desktop-first flows and forms.
 *   - 'bottom': slides up from the bottom edge with rounded top
 *     corners, like a mobile bottom sheet. Use for short
 *     confirmation flows, pickers, or anything driver/dispatcher
 *     would hit on a phone. Width caps at the same `size` token so
 *     a tablet still gets a centered card instead of a full-bleed
 *     sheet.
 */
type ModalPosition = 'center' | 'bottom';

interface ModalAction {
	label: string;
	onClick: () => void;
	variant?:
		| 'primary'
		| 'secondary'
		| 'outline'
		| 'ghost'
		| 'danger'
		| 'success';
	disabled?: boolean;
	className?: string;
}

interface ModalProps {
	isOpen: boolean;
	onClose: () => void;
	title?: string;
	description?: string;
	children?: React.ReactNode;
	size?: ModalSize;
	position?: ModalPosition;
	showCloseButton?: boolean;
	closeOnOverlayClick?: boolean;
	footer?: React.ReactNode;
	actions?: ModalAction[];
	className?: string;
	allowOverflow?: boolean;
}

const sizeClasses: Record<ModalSize, string> = {
	sm: 'max-w-md',
	md: 'max-w-lg',
	lg: 'max-w-2xl',
	xl: 'max-w-4xl',
	full: 'max-w-7xl',
};

export function Modal({
	isOpen,
	onClose,
	title,
	description,
	children,
	size = 'md',
	position = 'center',
	showCloseButton = true,
	closeOnOverlayClick = true,
	footer,
	actions,
	className,
	allowOverflow = false,
}: ModalProps): React.ReactElement {
	const colors = useThemeColors();

	const isBottom = position === 'bottom';

	return (
		<Transition appear show={isOpen} as={Fragment}>
			<Dialog
				as="div"
				className="relative z-50"
				onClose={closeOnOverlayClick ? onClose : (): void => {}}
			>
				{/* Backdrop */}
				<TransitionChild
					as={Fragment}
					enter="ease-out duration-300"
					enterFrom="opacity-0"
					enterTo="opacity-100"
					leave="ease-in duration-200"
					leaveFrom="opacity-100"
					leaveTo="opacity-0"
				>
					<div
						className={cn('fixed inset-0', colors.classes.backdropOverlay)}
					/>
				</TransitionChild>

				{/* Modal Panel */}
				<div className="fixed inset-0 overflow-y-auto custom-scrollbar">
					<div
						className={cn(
							'flex min-h-full text-center',
							isBottom
								? // P1.3 bottom sheet: align to bottom edge, no
									// outer padding so the panel runs flush to
									// the bottom (rounded only on top corners).
									'items-end justify-center'
								: 'items-center justify-center p-4',
						)}
					>
						<TransitionChild
							as={Fragment}
							enter="ease-out duration-300"
							enterFrom={
								isBottom ? 'opacity-0 translate-y-full' : 'opacity-0 scale-95'
							}
							enterTo={
								isBottom ? 'opacity-100 translate-y-0' : 'opacity-100 scale-100'
							}
							leave="ease-in duration-200"
							leaveFrom={
								isBottom ? 'opacity-100 translate-y-0' : 'opacity-100 scale-100'
							}
							leaveTo={
								isBottom ? 'opacity-0 translate-y-full' : 'opacity-0 scale-95'
							}
						>
							<DialogPanel
								className={cn(
									'w-full overflow-hidden text-left align-middle transition-all',
									isBottom
										? // Rounded only on top for the bottom-sheet
											// silhouette. No extra margin so it sits
											// flush. Cap height so very long content
											// scrolls inside the sheet instead of
											// pushing the action footer off-screen.
											'rounded-t-2xl max-h-[90vh]'
										: 'rounded-lg',
									'shadow-2xl dark:shadow-[0_25px_50px_-12px_rgba(0,0,0,0.75)]',
									'border border-default',
									'ring-1 ring-black ring-opacity-5',
									colors.classes.modalPanel,
									sizeClasses[size],
									className,
								)}
							>
								{/* Header */}
								{(title != null || showCloseButton) && (
									<div
										className={cn(
											'px-6 py-4 border-b',
											colors.classes.borderDefault,
										)}
									>
										<div className="flex items-center justify-between">
											<div>
												{title != null && (
													<DialogTitle
														as="h3"
														className={cn(
															'text-lg font-medium leading-6',
															colors.classes.textPrimary,
														)}
													>
														{title}
													</DialogTitle>
												)}
												{description != null && (
													<Description
														className={cn(
															'mt-1 text-sm',
															colors.classes.textSecondary,
														)}
													>
														{description}
													</Description>
												)}
											</div>
											{showCloseButton && (
												<button
													type="button"
													className={cn(
														// 44px tap target on touch: the 24px glyph alone was
														// far below Apple's minimum and the most-used
														// dismissal control in the app (PD-15818). The
														// negative margin keeps the visual alignment.
														'rounded-md inline-flex items-center justify-center p-2.5 -m-2.5',
														colors.classes.textSecondary,
														`hover:${colors.classes.textPrimary}`,
														colors.classes.focusRingPrimary,
													)}
													onClick={onClose}
												>
													<svg
														className="h-6 w-6"
														fill="none"
														viewBox="0 0 24 24"
														stroke="currentColor"
													>
														<path
															strokeLinecap="round"
															strokeLinejoin="round"
															strokeWidth={2}
															d="M6 18L18 6M6 6l12 12"
														/>
													</svg>
												</button>
											)}
										</div>
									</div>
								)}

								{/* Body */}
								{children != null && (
									<div
										className={cn(
											'px-6 py-4',
											!allowOverflow &&
												'max-h-[calc(100vh-150px)] overflow-y-auto custom-scrollbar',
										)}
									>
										{children}
									</div>
								)}

								{/* Footer */}
								{(footer != null || actions != null) && (
									<div
										className={cn(
											'px-6 py-4 border-t',
											colors.classes.bgSecondary,
											colors.classes.borderDefault,
										)}
									>
										{footer}
										{actions != null && (
											<div className="flex justify-end gap-3">
												{actions.map((action, index) => (
													<Button
														key={index}
														onClick={action.onClick}
														variant={action.variant}
														disabled={action.disabled}
														className={action.className}
													>
														{action.label}
													</Button>
												))}
											</div>
										)}
									</div>
								)}
							</DialogPanel>
						</TransitionChild>
					</div>
				</div>
			</Dialog>
		</Transition>
	);
}
